import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";

/**
 * Fotobox, Photobooth, Fotoautomat — ein Gerät, viele Namen.
 *
 * Die Search Console zeigt, dass die Leute mit sehr verschiedenen Wörtern
 * suchen: "leihen" und "ausleihen" bringen zusammen 25.469 Impressionen,
 * "photobooth" 17.812, "fotoautomat" 15.370. Auf der Startseite kam das
 * Wort "leihen" bis dahin kein einziges Mal vor.
 *
 * Der Abschnitt steht hier nicht, um Wörter unterzubringen, sondern weil er
 * eine echte Frage beantwortet: Wer nach "Fotoautomat mieten" sucht, weiß
 * oft nicht, ob er dasselbe bekommt wie unter "Fotobox".
 */

const NAMEN = [
  {
    wort: "Photobooth",
    herkunft: "Das englische Original.",
    erklaerung:
      "So heißt das Gerät im Englischen, und weil die ersten Anbieter aus den USA kamen, ist der Name auch hier geblieben. Manche schreiben Fotobooth — gemeint ist dasselbe.",
  },
  {
    wort: "Fotoautomat",
    herkunft: "Erinnert an die Passbildkabine.",
    erklaerung:
      "Der Name kommt von den Automaten im Bahnhof. Gemeint ist heute meist dasselbe Mietgerät; nur wenige Anbieter vermieten tatsächlich alte Kabinen mit Vorhang.",
  },
  {
    wort: "Fotokiste",
    herkunft: "Die eingedeutschte Variante.",
    erklaerung:
      "Seltener, aber gebräuchlich — vor allem für die kompakten Modelle im Holzgehäuse.",
  },
];

export default function BegriffeSektion() {
  return (
    <SectionHeader
      id="begriffe"
      title="Fotobox, Photobooth oder Fotoautomat?"
      subtitle="Drei Namen, ein Gerät — und der eine Unterschied, auf den es wirklich ankommt."
    >
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        <p>
          Ob ihr eine Fotobox mieten, ein Photobooth leihen oder einen
          Fotoautomaten ausleihen wollt: Gemeint ist in aller Regel dasselbe —
          eine Kamera mit Bildschirm, Beleuchtung und meist einem Drucker, die
          eure Gäste selbst bedienen. Auch zwischen{" "}
          <strong className="font-semibold text-slate-900">mieten</strong>,{" "}
          <strong className="font-semibold text-slate-900">leihen</strong> und{" "}
          <strong className="font-semibold text-slate-900">ausleihen</strong>{" "}
          gibt es keinen Unterschied; alle drei Wörter meinen dieselbe Sache.
        </p>

        <dl className="mt-6 space-y-4">
          {NAMEN.map((n) => (
            <div key={n.wort} className="border-l-2 border-slate-200 pl-5">
              <dt className="font-semibold text-slate-900">
                {n.wort}{" "}
                <span className="font-normal text-slate-500">
                  — {n.herkunft}
                </span>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-slate-600">
                {n.erklaerung}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-6">
          Worauf es beim Vergleich wirklich ankommt, ist nicht der Name,
          sondern die{" "}
          <Link
            href="#bauformen"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Bauform
          </Link>
          : eine klassische Box auf dem Stativ braucht anderthalb Quadratmeter,
          eine Spiegel-Fotobox wirkt als Möbelstück im Raum, eine geschlossene
          Kabine gibt Gästen mehr Mut. Danach solltet ihr suchen — und nach der
          Druckleistung, die für eure Gästezahl reicht.
        </p>
      </div>
    </SectionHeader>
  );
}
